import { useEffect, useRef } from 'react';

const FFT_SIZE = 512;
const NOISE_FLOOR = 0.02;
const SPEECH_CEILING = 0.2;
const ATTACK = 0.45;
const RELEASE = 0.08;
const SILENCE_EPSILON = 0.02;

interface Options {
  stream: MediaStream | null;
  onLevel: (level: number) => void;
  isEnabled?: boolean;
}

type WindowWithLegacyAudio = typeof window & {
  webkitAudioContext?: typeof AudioContext;
};

const resolveAudioContext = () =>
  window.AudioContext ?? (window as WindowWithLegacyAudio).webkitAudioContext;

const readRms = (samples: Uint8Array) => {
  const sum = samples.reduce((accumulator, sample) => {
    const deviation = (sample - 128) / 128;

    return accumulator + deviation * deviation;
  }, 0);

  return Math.sqrt(sum / samples.length);
};

const normalizeRms = (rms: number) =>
  Math.min(
    Math.max((rms - NOISE_FLOOR) / (SPEECH_CEILING - NOISE_FLOOR), 0),
    1
  );

export const useAudioLevel = ({
  stream,
  onLevel,
  isEnabled = true,
}: Options) => {
  const onLevelRef = useRef(onLevel);
  onLevelRef.current = onLevel;

  useEffect(() => {
    const AudioContextConstructor = resolveAudioContext();

    if (!stream || !isEnabled || !AudioContextConstructor) return undefined;

    const context = new AudioContextConstructor();
    const analyser = context.createAnalyser();
    analyser.fftSize = FFT_SIZE;

    context.createMediaStreamSource(stream).connect(analyser);

    const samples = new Uint8Array(analyser.fftSize);
    let smoothed = 0;
    let frameId = 0;

    const tick = () => {
      analyser.getByteTimeDomainData(samples);

      const target = normalizeRms(readRms(samples));
      smoothed += (target - smoothed) * (target > smoothed ? ATTACK : RELEASE);

      onLevelRef.current(smoothed < SILENCE_EPSILON ? 0 : smoothed);
      frameId = requestAnimationFrame(tick);
    };

    context.resume().catch(() => undefined);
    frameId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(frameId);
      context.close().catch(() => undefined);
    };
  }, [isEnabled, stream]);
};
